import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Users, MessageSquare, Activity, Clock, TrendingUp, Star,
  Zap, Loader2, AlertTriangle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function useAdminAnalytics() {
  const [state, setState] = useState({ loading: true, error: '', data: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ov, dt, fu, tu, ss] = await Promise.all([
          axios.get(`${API}/api/analytics/dashboard/overview`),
          axios.get(`${API}/api/analytics/dashboard/daily-trends?days=14`),
          axios.get(`${API}/api/analytics/dashboard/feature-usage?days=30`),
          axios.get(`${API}/api/analytics/dashboard/top-users?limit=5`),
          axios.get(`${API}/api/analytics/dashboard/session-stats?days=30`),
        ]);
        if (cancelled) return;
        setState({
          loading: false, error: '',
          data: {
            overview: ov.data || {},
            dailyTrends: dt.data || [],
            featureUsage: fu.data || [],
            topUsers: tu.data || [],
            sessionStats: ss.data || {},
          },
        });
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, error: 'Could not load activity metrics.', data: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return state;
}

function formatDuration(sec) {
  if (!sec) return '0s';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s ? `${m}m ${s}s` : `${m}m`;
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

/**
 * ActivityMetrics — modern hero section for the Admin Dashboard.
 * Shows: 4 stat cards + 14-day daily trend chart + feature breakdown + top users.
 */
export default function ActivityMetrics() {
  const { loading, error, data } = useAdminAnalytics();

  if (loading) {
    return (
      <div className="am-hero" data-testid="admin-activity-metrics">
        <style>{amStyles}</style>
        <div className="am-empty"><Loader2 size={16} className="am-spin" /> Loading activity metrics…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="am-hero" data-testid="admin-activity-metrics">
        <style>{amStyles}</style>
        <div className="am-empty am-error">
          <AlertTriangle size={16} /> {error || 'No metrics available yet.'}
        </div>
      </div>
    );
  }

  return (
    <section className="am-hero" data-testid="admin-activity-metrics">
      <style>{amStyles}</style>

      <header className="am-hero-head">
        <div>
          <h2 className="am-hero-title">
            <Activity size={16} /> Activity Metrics
          </h2>
          <p className="am-hero-sub">Live snapshot of steward engagement, powered by the analytics pipeline.</p>
        </div>
      </header>

      <StatCards data={data} />

      <div className="am-grid">
        <DailyTrendChart trends={data.dailyTrends} />
        <FeatureBreakdown features={data.featureUsage} />
      </div>

      <TopUsersPanel users={data.topUsers} />
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function StatCards({ data }) {
  const { overview, sessionStats } = data;
  const cards = [
    { key: 'users',       Icon: Users,         color: '#22d3ee',
      label: 'Total Stewards',  value: overview.totalUsers ?? 0,
      hint: `${overview.activeUsers ?? 0} active this month` },
    { key: 'questions',   Icon: MessageSquare, color: '#a78bfa',
      label: 'Questions Answered', value: overview.totalQuestions ?? 0,
      hint: `${overview.helpfulRate ? Math.round(overview.helpfulRate * 100) + '% helpful' : 'no feedback yet'}` },
    { key: 'sessions',    Icon: Zap,           color: '#fbbf24',
      label: 'Sessions',        value: overview.totalSessions ?? 0,
      hint: `${overview.activeUsersToday ?? 0} active today` },
    { key: 'avgTime',     Icon: Clock,         color: '#22c55e',
      label: 'Avg Session',     value: formatDuration(sessionStats.avgDuration ?? 0),
      hint: `${sessionStats.completedSessions ?? 0} completed` },
  ];
  return (
    <div className="am-cards" data-testid="admin-stat-cards">
      {cards.map((c) => (
        <div key={c.key} className="am-card" style={{ '--am-c': c.color }} data-testid={`admin-stat-${c.key}`}>
          <span className="am-card-icon"><c.Icon size={18} /></span>
          <span className="am-card-label">{c.label}</span>
          <span className="am-card-value">{c.value}</span>
          <span className="am-card-hint">{c.hint}</span>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function DailyTrendChart({ trends }) {
  const max = useMemo(
    () => Math.max(1, ...trends.map((d) => d.events || 0)),
    [trends],
  );

  return (
    <div className="am-panel" data-testid="admin-daily-trend">
      <header className="am-panel-head">
        <TrendingUp size={14} />
        <h3>Events · Last 14 days</h3>
      </header>
      <div className="am-bars">
        {trends.map((d) => {
          const h = ((d.events || 0) / max) * 100;
          return (
            <div key={d.date} className="am-bar-wrap" data-testid={`admin-daily-bar-${d.date}`}>
              <div
                className="am-bar"
                style={{ height: `${Math.max(2, h)}%` }}
                title={`${d.date} · ${d.events || 0} events · ${d.uniqueUsers || 0} users`}
              />
              <span className="am-bar-x">{d.date.slice(5)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

const FEATURE_COLORS = {
  ask_stewie:   '#22d3ee',
  fact_finder:  '#fbbf24',
  job_briefing: '#a78bfa',
  case_locker:  '#22c55e',
  library:      '#fb923c',
  login:        '#94a3b8',
};

function FeatureBreakdown({ features }) {
  const total = features.reduce((s, f) => s + (f.count || 0), 0) || 1;
  return (
    <div className="am-panel" data-testid="admin-feature-breakdown">
      <header className="am-panel-head">
        <Activity size={14} />
        <h3>Feature Usage · 30 days</h3>
      </header>
      <div className="am-features">
        {features.length === 0 && <div className="am-empty">No feature usage recorded yet.</div>}
        {features.map((f) => {
          const color = FEATURE_COLORS[f.feature] || '#94a3b8';
          const pct = ((f.count || 0) / total) * 100;
          return (
            <div key={f.feature} className="am-feature" data-testid={`admin-feature-${f.feature}`}>
              <span className="am-feature-label" style={{ color }}>
                {(f.feature || '').replace(/_/g, ' ')}
              </span>
              <span className="am-feature-track">
                <span className="am-feature-fill" style={{ width: `${pct}%`, background: color }} />
              </span>
              <span className="am-feature-count">{f.count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

function TopUsersPanel({ users }) {
  return (
    <div className="am-panel" data-testid="admin-top-users">
      <header className="am-panel-head">
        <Star size={14} />
        <h3>Top Active Stewards</h3>
      </header>
      {users.length === 0 ? (
        <div className="am-empty">No activity yet.</div>
      ) : (
        <ol className="am-users">
          {users.map((u, i) => (
            <li key={u.userId || u.email || i} className="am-user" data-testid={`admin-top-user-${i}`}>
              <span className="am-user-rank">#{i + 1}</span>
              <span className="am-user-main">
                <span className="am-user-name">{u.name || u.email || 'Unknown'}</span>
                <span className="am-user-email">{u.email}</span>
              </span>
              <span className="am-user-events">{u.eventCount ?? 0} events</span>
              {u.lastActive && (
                <span className="am-user-time">{formatDate(u.lastActive)}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

const amStyles = `
.am-hero {
  background: linear-gradient(180deg, rgba(15,27,70,0.55) 0%, rgba(15,27,70,0.25) 100%);
  border: 1px solid rgba(148,163,184,0.18);
  border-radius: 16px;
  padding: 20px;
  margin: 20px 0 24px;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif;
  color: #e2e8f0;
}
.am-hero-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
.am-hero-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 16px; font-weight: 800; letter-spacing: -0.01em;
  color: #f1f5f9; margin: 0;
}
.am-hero-sub {
  font-size: 12px; color: #94a3b8;
  margin: 4px 0 0;
}

.am-empty {
  padding: 24px;
  text-align: center;
  color: #94a3b8; font-size: 13px;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.am-error { color: #fecaca; }

.am-spin { animation: am-spin-kf 1.2s linear infinite; }
@keyframes am-spin-kf { to { transform: rotate(360deg); } }

/* Stat cards */
.am-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}
@media (min-width: 720px) { .am-cards { grid-template-columns: repeat(4, 1fr); } }
.am-card {
  padding: 12px;
  background: rgba(10,14,26,0.55);
  border: 1px solid rgba(148,163,184,0.18);
  border-left: 3px solid var(--am-c, #22d3ee);
  border-radius: 12px;
  display: flex; flex-direction: column; gap: 4px;
}
.am-card-icon {
  width: 28px; height: 28px; border-radius: 8px;
  display: grid; place-items: center;
  color: var(--am-c, #22d3ee);
  background: color-mix(in oklab, var(--am-c, #22d3ee) 14%, transparent);
  border: 1px solid color-mix(in oklab, var(--am-c, #22d3ee) 40%, transparent);
  margin-bottom: 2px;
}
.am-card-label { font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; }
.am-card-value { font-size: 22px; font-weight: 800; color: #f1f5f9; letter-spacing: -0.02em; line-height: 1.2; }
.am-card-hint { font-size: 11.5px; color: #94a3b8; }

/* Panels */
.am-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 12px;
}
@media (min-width: 900px) { .am-grid { grid-template-columns: 1.4fr 1fr; } }

.am-panel {
  background: rgba(10,14,26,0.55);
  border: 1px solid rgba(148,163,184,0.18);
  border-radius: 12px;
  padding: 14px;
}
.am-panel-head {
  display: flex; align-items: center; gap: 6px;
  color: #94a3b8;
  margin-bottom: 10px;
}
.am-panel-head h3 {
  margin: 0; font-size: 12px; font-weight: 800; letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #cbd5e1;
}

/* Daily trend bars */
.am-bars {
  display: flex; align-items: flex-end; gap: 4px;
  height: 120px;
  padding-bottom: 18px;
  position: relative;
}
.am-bar-wrap {
  flex: 1;
  display: flex; flex-direction: column; align-items: center;
  height: 100%; position: relative;
}
.am-bar {
  width: 100%;
  background: linear-gradient(180deg, #22d3ee, rgba(34,211,238,0.35));
  border-radius: 4px 4px 0 0;
  min-height: 2px;
  transition: transform 200ms, filter 200ms;
}
.am-bar:hover { filter: brightness(1.25); transform: scaleY(1.02); }
.am-bar-x {
  position: absolute; bottom: -18px;
  font-size: 9px; color: #64748b;
  transform: rotate(-45deg); transform-origin: center top;
  white-space: nowrap;
}

/* Feature bars */
.am-features { display: flex; flex-direction: column; gap: 8px; }
.am-feature {
  display: grid;
  grid-template-columns: 110px 1fr 40px;
  align-items: center;
  gap: 10px;
}
.am-feature-label {
  font-size: 12px; font-weight: 700;
  text-transform: capitalize;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.am-feature-track {
  height: 8px;
  background: rgba(148,163,184,0.14);
  border-radius: 4px;
  overflow: hidden;
}
.am-feature-fill {
  display: block; height: 100%;
  border-radius: 4px;
  transition: width 320ms ease-out;
}
.am-feature-count {
  font-size: 12px; font-weight: 700; color: #cbd5e1;
  text-align: right;
}

/* Top users */
.am-users {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 6px;
}
.am-user {
  display: grid;
  grid-template-columns: 32px 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(148,163,184,0.04);
  border: 1px solid rgba(148,163,184,0.10);
  border-radius: 10px;
}
.am-user-rank {
  font-weight: 800; font-size: 12px;
  color: #fbbf24;
  background: rgba(251,191,36,0.10);
  border: 1px solid rgba(251,191,36,0.35);
  border-radius: 6px;
  padding: 3px 0;
  text-align: center;
}
.am-user-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.am-user-name  { font-size: 13px; font-weight: 700; color: #f1f5f9; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.am-user-email { font-size: 11px; color: #94a3b8; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.am-user-events{ font-size: 11.5px; font-weight: 700; color: #22d3ee; white-space: nowrap; }
.am-user-time  { font-size: 10.5px; color: #64748b; white-space: nowrap; }
@media (max-width: 480px) {
  .am-user { grid-template-columns: 28px 1fr auto; }
  .am-user-time { display: none; }
}
`;
